import AppButton from '@nxsys/components/AppButton';
import { AppConfirmation } from '@nxsys/components/AppConfirmationModal';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { showMessage } from '@nxsys/utils/message';
import { ColumnsType } from 'antd/es/table';
import { AppState } from '@nxsys/store';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getCISImportXMLColumns,
  getFPSImportXMLColumns,
} from '../listing/columns';
import UploadedFileInformation from './UploadedFileInformation';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import {
  useSaveImportXMLEmployee,
  useValidateImportXMLEmployee,
} from '@nxsys/hooks/api/import';
import { Option } from '@nxsys/types/models/Common';

type ImportXMLResultUploadModalProps = {
  isOpen: boolean;
  toggle: () => void;
  resultData: IImportEmployee | null;
  loading: boolean;
};

const ImportXMLResultUploadModal: React.FC<ImportXMLResultUploadModalProps> = ({
  isOpen,
  toggle,
  resultData,
  loading,
}) => {
  const { messages, formatMessage } = useTypedIntl();
  const { payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );

  const [isValidated, setIsValidated] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Option<string> | null>(
    null,
  );
  const [employeeData, setEmployeeData] = useState<IImportEmployeeXmlType[]>(
    [],
  );

  const { fileName, fileSize, xmlType, id } = resultData || {};

  useEffect(() => {
    setEmployeeData(resultData?.employees ?? []);
  }, [resultData]);

  const isCISData = xmlType === 'CIS300';

  const fpsColumns = useMemo(
    (): ColumnsType<IImportEmployeeXmlType> =>
      getFPSImportXMLColumns({ messages, payrollCountry }),
    [messages, payrollCountry],
  );

  const cisColumns = useMemo(
    (): ColumnsType<IImportEmployeeXmlType> =>
      getCISImportXMLColumns({ messages }),
    [messages],
  );

  const columns: any = useMemo(() => {
    return [
      ...(isCISData ? cisColumns : fpsColumns),
      ...(isValidated
        ? [
            {
              key: 'status',
              title: messages['common.status'],
              width: 100,
              render: (record: IImportEmployeeXmlType) => {
                if (record.isDuplicate) {
                  return (
                    <span style={{ color: 'orange' }}>
                      {messages['common.duplicate']}
                    </span>
                  );
                }
                if (
                  record.validationErrors &&
                  record.validationErrors.length > 0
                ) {
                  return (
                    <span style={{ color: 'red' }}>
                      {messages['common.error']}
                    </span>
                  );
                }
                return (
                  <span style={{ color: 'green' }}>
                    {messages['common.valid']}
                  </span>
                );
              },
            },
            {
              key: 'validationMessages',
              title: messages['common.validationMessages'],
              width: 250,
              render: (record: IImportEmployeeXmlType) => {
                if (
                  record.validationErrors &&
                  record.validationErrors.length > 0
                ) {
                  return (
                    <span style={{ color: 'red' }}>
                      {record.validationErrors.join('; ')}
                    </span>
                  );
                }
                if (record.isDuplicate) {
                  return (
                    <span style={{ color: 'orange' }}>
                      {messages['common.duplicateEmployeeFound']}
                    </span>
                  );
                }
              },
            },
          ]
        : []),
    ];
  }, [isCISData, cisColumns, fpsColumns, isValidated]);

  const scrollWidth = isCISData ? 2800 : 10000;
  const totalRecords = employeeData?.length ?? 0;

  const {
    run: validateImportXMLEmployee,
    loading: validateImportXMLEmployeeLoading,
  } = useValidateImportXMLEmployee();

  const { run: saveImportXMLEmployee } = useSaveImportXMLEmployee();

  const handleSaveImport = () => {
    if (!id || !selectedCompany) return;
    saveImportXMLEmployee({
      companyPaymentId: selectedCompany?.value,
      id,
    }).then(() => {
      showMessage({
        type: 'info',
        content: messages['import.employee.importInProgress'],
      });
      toggle();
    });
  };

  const handleValidate = async () => {
    if (!id) return;

    if (!selectedCompany) {
      showMessage({
        type: 'warning',
        content: messages['common.pleaseSelectCompany'],
      });
      return;
    }

    const res = await validateImportXMLEmployee({
      companyPaymentId: selectedCompany?.value,
      id,
    });
    const { totalInvalidEmployees, employees } = res;
    setEmployeeData(employees);
    setIsValidated(true);

    const invalidRecords = employees.filter(
      (record) => record.validationErrors && record.validationErrors.length > 0,
    );

    if (totalInvalidEmployees === employees.length && employees.length > 0) {
      AppConfirmation.open({
        title: messages['common.validate.error'],
        paragraph: messages['import.importError'],
        type: 'warning',
        icon: 'IconWarning',
        showFooterButton: false,
      });
      return;
    }

    if (invalidRecords.length > 0) {
      AppConfirmation.open({
        title: messages['common.validate.error'],
        paragraph:
          messages[
            'import.someRecordsHaveValidationErrorsButValidRecordsCanStillBeImported'
          ],
        type: 'warning',
        icon: 'IconWarning',
        okText: messages['common.importValidRecord'],
        onConfirm: () => {
          const validRecords = employees.filter(
            (record) =>
              !record.validationErrors || record.validationErrors.length === 0,
          );
          if (!validRecords?.length) {
            showMessage({
              type: 'error',
              content: messages['import.noValidRecordToImport'],
            });
            return;
          }
          handleSaveImport();
        },
      });
    }

    AppConfirmation.open({
      title: messages['common.success'],
      paragraph: formatMessage(
        { id: 'import.successMappedFields' },
        { fileName },
      ),
      onConfirm: handleSaveImport,
      type: 'success',
      icon: 'IconCheck',
    });
  };

  return (
    <AppModalContainer
      isOpen={isOpen}
      toggle={toggle}
      title={messages['bulkUpdate.modal.fileInformation']}
      width="90%"
      minHeight="800px"
    >
      <div className="flex justify-between mb-4 flex-wrap">
        <UploadedFileInformation
          fileName={fileName}
          fileSize={fileSize}
          totalRecords={totalRecords}
        />
        <div className="flex gap-4">
          <AppSelectSync
            type="company"
            apiType="searchCompany"
            placeholder={messages['form.selectCompany']}
            onChangeValue={(value: any) => {
              setSelectedCompany(value);
            }}
          />
          <AppButton
            name={messages['common.validate']}
            type="primary"
            onClick={handleValidate}
            loading={validateImportXMLEmployeeLoading}
            disabled={loading || validateImportXMLEmployeeLoading}
          />
        </div>
      </div>

      <AppTableContainer
        data={employeeData}
        loading={loading}
        page={0}
        total={totalRecords}
        pageSize={totalRecords}
        columns={columns}
        scroll={{ x: !isValidated ? scrollWidth : scrollWidth + 700, y: 500 }}
        {...(totalRecords > 1000 ? { virtual: true } : {})}
      />
    </AppModalContainer>
  );
};

export default ImportXMLResultUploadModal;
